import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { cloneDeep, isObject } from 'lodash';
import Feature from 'ol/Feature.js';
import map from 'ol/Map.js';
import Overlay from 'ol/Overlay';
import View from 'ol/View.js';
import { Point } from 'ol/geom.js';
import { Tile as TileLayer, Vector as VectorLayer } from 'ol/layer';
import { fromLonLat } from 'ol/proj.js';
import OSM from 'ol/source/OSM.js';
import VectorSource from 'ol/source/Vector.js';
import { Icon, Style } from 'ol/style';
import { GeoMapLocationItem } from 'src/app/interfaces/geo-map-location-item';
import { ToastService } from 'src/app/services/toast.service';

@Component({
  selector: 'app-geo-map',
  templateUrl: './geo-map.component.html',
  styleUrls: ['./geo-map.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class GeoMapComponent implements OnInit, OnChanges {
  constructor(private ToastService: ToastService) {}

  @ViewChild('map') geoMapElement?: ElementRef;
  @ViewChild('popup') popupElement?: ElementRef;
  @ViewChild('popupText') popupTextElement?: ElementRef;
  @ViewChild('additionalPopupText') additionalPopupTextElement?: ElementRef;
  @Input() coordinateInfo: GeoMapLocationItem[] = [];

  CountryToCoordinateMap: {
    US: [number, number];
    CA: [number, number];
  } = {
    US: [-95.7129, 37.0902],
    CA: [-106.3468, 56.1304]
  };

  // Default is positioned over the United States in Longitude/Latitude format.
  @Input() DefaultCoordinate = this.CountryToCoordinateMap['US'];

  // This represents the coordinate the map is centered on. We can dynamically update this to zoom in on
  // a specific coordinate on the map.
  @Input() centeredCoordinate?: [number, number] | null;
  @Input() zoomLevel?: number = 4;

  @Output() featureClick = new EventEmitter<GeoMapLocationItem>();

  LongitudeLatitudeCoordinateInfoMap: { [key: string]: GeoMapLocationItem } =
    {};

  geoMap?: map;

  popupText: string = '';
  additionalPopupText: string[] = [];

  mapCoordinatesToFeatureSources() {
    return this.coordinateInfo.map((coordinate) => {
      const { name, points, additionalText, src } = coordinate;
      const [longitude, latitude] = points;

      this.LongitudeLatitudeCoordinateInfoMap[
        `${Math.floor(longitude * 10000) / 10000},${Math.floor(latitude * 10000) / 10000}`
      ] = coordinate;

      const featureObject = {
        type: 'geoMarker',
        name,
        geometry: new Point(fromLonLat(points))
      };

      if (additionalText) {
        Object.assign(featureObject, { additionalText });
      }

      const feature: Feature = new Feature(featureObject);

      feature.setStyle(
        new Style({
          image: new Icon({
            src: src || '/assets/images/geo-alt-fill.svg',
            scale: 2
          })
        })
      );

      return feature;
    });
  }

  addFeatures() {
    if (!this.geoMap) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Geo Map has not been initialized.',
        { returnError: true }
      );
    }

    const Features = this.mapCoordinatesToFeatureSources();

    this.geoMap.getLayers().forEach((layer) => {
      if (layer instanceof VectorLayer) {
        this.geoMap!.removeLayer(layer);
      }
    });

    if (Features.length > 0) {
      const vectorSource = new VectorSource({
        features: Features
      });

      const vectorLayer = new VectorLayer({
        source: vectorSource
      });

      this.geoMap.addLayer(vectorLayer);
    }
  }

  centerOnCoordinate() {
    if (!this.geoMap) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There was an error initializing the geo map.',
        { returnError: true }
      );
    }

    this.geoMap.setView(
      new View({
        center: fromLonLat(this.centeredCoordinate || this.DefaultCoordinate),
        zoom: this.centeredCoordinate ? 14 : this.zoomLevel
      })
    );
  }

  ngAfterViewInit() {
    if (!this.geoMapElement) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There was an error rendering the geo map element.',
        { returnError: true }
      );
    }

    if (!this.popupElement) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There was an error rendering the popup element.',
        { returnError: true }
      );
    }

    if (!this.popupTextElement) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There was an error rendering the popup text element.',
        { returnError: true }
      );
    }

    if (!this.additionalPopupTextElement) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There was an error rendering the additional popup text element.',
        { returnError: true }
      );
    }

    const tileLayer = new TileLayer({ source: new OSM() });

    const popupOverlay = new Overlay({
      element: this.popupElement.nativeElement
    });

    this.geoMap = new map({
      view: new View({
        center: fromLonLat(this.centeredCoordinate || this.DefaultCoordinate),
        zoom: this.zoomLevel
      }),
      overlays: [popupOverlay],
      layers: [tileLayer],
      target: this.geoMapElement.nativeElement
    });

    this.geoMap.on('pointermove', (event) => {
      const { nativeElement } = this.geoMapElement!;
      nativeElement.style.cursor = 'default';
      this.geoMap?.forEachFeatureAtPixel(event.pixel, (feature) => {
        nativeElement.style.cursor = 'pointer';
      });
    });

    this.geoMap.on('singleclick', (event) => {
      const { nativeElement: popupTextNativeElement } = this.popupTextElement!;
      const { nativeElement: additionalPopupTextNativeElement } =
        this.additionalPopupTextElement!;
      const { coordinate } = event;
      popupOverlay.setPosition(undefined);
      this.geoMap?.forEachFeatureAtPixel(event.pixel, (feature) => {
        const geometry = feature.getGeometry();

        if (geometry) {
          const point = cloneDeep(geometry).transform(
            'EPSG:3857',
            'EPSG:4326'
          ) as Point;
          const featureCoordinates = point.getCoordinates();
          const [longitude, latitude] = featureCoordinates;
          const coordinateInfo =
            this.LongitudeLatitudeCoordinateInfoMap[
              `${Math.floor(longitude * 10000) / 10000},${Math.floor(latitude * 10000) / 10000}`
            ];

          if (!coordinateInfo) {
            throw this.ToastService.addErrorToast(
              'Error',
              'There was an error selecting the map feature.',
              { returnError: true }
            );
          }

          this.featureClick.emit(coordinateInfo);
        }

        popupTextNativeElement.innerText = feature.get('name');
        const additionalText = feature.get('additionalText');

        if (additionalText) {
          additionalPopupTextNativeElement.innerText =
            additionalText.join('\n');
        } else {
          additionalPopupTextNativeElement.innerText = '';
        }
        popupOverlay.setPosition(coordinate);
      });
    });

    if (this.coordinateInfo.length > 0) {
      this.addFeatures();
    }

    this.centerOnCoordinate();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { coordinateInfo, centeredCoordinate } = changes;

    if (this.geoMap) {
      if (isObject(coordinateInfo)) {
        this.addFeatures();
      }

      if (isObject(centeredCoordinate) || centeredCoordinate === null) {
        this.centerOnCoordinate();
      }
    }
  }

  ngOnInit(): void {}
}
